/**
 * useWebcam — manages a getUserMedia video stream lifecycle.
 *
 * Handles: permission request, stream start/stop, cleanup on unmount.
 * The returned videoRef should be attached to a <video> element.
 *
 * States:
 *   idle        → initial; stream not yet requested
 *   requesting  → awaiting getUserMedia permission
 *   streaming   → stream active, video element live
 *   denied      → permission denied or not supported
 *   error       → unexpected error
 */
import { useCallback, useEffect, useRef, useState } from 'react'

export type WebcamStatus = 'idle' | 'requesting' | 'streaming' | 'denied' | 'error'

export interface UseWebcamReturn {
  videoRef:     React.RefObject<HTMLVideoElement | null>
  status:       WebcamStatus
  errorMessage: string | null
  start:        () => Promise<void>
  stop:         () => void
  capture:      () => Blob | null   // captures current frame → JPEG Blob
}

export function useWebcam(): UseWebcamReturn {
  const videoRef   = useRef<HTMLVideoElement>(null)
  const streamRef  = useRef<MediaStream | null>(null)
  const [status, setStatus]       = useState<WebcamStatus>('idle')
  const [errorMessage, setError]  = useState<string | null>(null)

  // Stop stream and revoke tracks
  const stop = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
    setStatus('idle')
  }, [])

  // Start webcam stream
  const start = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus('denied')
      setError('Camera access is not supported in this browser.')
      return
    }

    setStatus('requesting')
    setError(null)

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      })
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play().catch(() => {
          // Autoplay may be blocked — user action will resume it
        })
      }
      setStatus('streaming')
    } catch (err: unknown) {
      const name = (err instanceof Error) ? err.name : ''
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        setStatus('denied')
        setError('Camera permission denied. Please allow camera access in your browser settings.')
      } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        setStatus('denied')
        setError('No camera found. Please connect a camera and try again.')
      } else {
        setStatus('error')
        setError('Could not access camera. Please try again.')
      }
    }
  }, [])

  // Capture the current video frame as a JPEG Blob
  const capture = useCallback((): Blob | null => {
    const video = videoRef.current
    if (!video || status !== 'streaming') return null

    const canvas = document.createElement('canvas')
    canvas.width  = video.videoWidth  || 1280
    canvas.height = video.videoHeight || 720
    const ctx = canvas.getContext('2d')
    if (!ctx) return null

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

    // Synchronous data URL → Blob conversion
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85)
    const [header, base64] = dataUrl.split(',')
    const mimeMatch = header.match(/:(.*?);/)
    const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg'
    const binary = atob(base64)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
    return new Blob([bytes], { type: mime })
  }, [status])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stop()
    }
  }, [stop])

  return { videoRef, status, errorMessage, start, stop, capture }
}
